import type { Comic } from '@/mocks/comics';

interface Props {
  comic: Comic;
  followed: boolean;
  onFollow: (id: number) => void;
  onOpen: (comic: Comic) => void;
}

function formatLikes(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
  return `${n}`;
}

export default function ComicCard({ comic, followed, onFollow, onOpen }: Props) {
  return (
    <article
      className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:shadow-soft"
      data-product-shop
    >
      <button
        type="button"
        onClick={() => onOpen(comic)}
        className="relative block aspect-[3/4] w-full overflow-hidden bg-background-100 text-left"
        aria-label={`查看《${comic.title}》详情`}
      >
        <img
          src={comic.cover}
          alt={`${comic.title} 搞笑漫画封面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs text-background-50 ${
            comic.status === '连载中' ? 'bg-accent-500' : 'bg-foreground-700/80'
          }`}
        >
          {comic.status}
        </span>
        <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-foreground-950/55 px-2.5 py-1 text-xs text-background-50 backdrop-blur-sm">
          <i className="ri-heart-3-fill text-accent-400" aria-hidden="true"></i>
          {formatLikes(comic.likes)}
        </span>
      </button>

      <div className="flex flex-1 flex-col p-3 md:p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate font-heading text-base text-foreground-950">
            <button type="button" onClick={() => onOpen(comic)} className="hover:text-primary-600">
              {comic.title}
            </button>
          </h3>
          <button
            type="button"
            onClick={() => onFollow(comic.id)}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
              followed
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
            }`}
            aria-label={followed ? '取消追更' : '追更'}
          >
            <i className={followed ? 'ri-star-fill' : 'ri-star-line'} aria-hidden="true"></i>
          </button>
        </div>

        <div className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
          <span>{comic.author}</span>
          <span className="text-foreground-300">|</span>
          <span>第 {comic.chapters} 话</span>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {comic.tags.map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
              {t}
            </span>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-time-line" aria-hidden="true"></i>
            {comic.updateTime}
          </span>
          <button
            type="button"
            onClick={() => onOpen(comic)}
            className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            立即阅读
          </button>
        </div>
      </div>
    </article>
  );
}